import type { StabilityScore } from '../../types/benchmark';

interface StabilityScoreCardProps {
  stability: StabilityScore;
}

export function StabilityScoreCard({ stability }: StabilityScoreCardProps) {
  const getScoreColor = (score: number) => {
    if (score >= 0.95) return 'text-green-600';
    if (score >= 0.85) return 'text-blue-600';
    if (score >= 0.70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 0.95) return 'Excellent';
    if (score >= 0.85) return 'Bon';
    if (score >= 0.70) return 'Moyen';
    return 'Problématique';
  };

  const scorePercentage = stability.stability_score * 100;

  return (
    <div className="bg-white border rounded-lg p-6">
      <h3 className="text-lg font-semibold mb-4">Score de stabilité</h3>

      {!stability.config_match ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
          <p className="text-sm text-yellow-800">
            Les runs doivent avoir la même configuration (modèle et prompt) pour calculer la stabilité
          </p>
        </div>
      ) : (
        <>
          {/* Score gauge */}
          <div className="text-center mb-6">
            <div className={`text-5xl font-bold ${getScoreColor(Number(stability.stability_score))} mb-2`}>
              {scorePercentage.toFixed(1)}%
            </div>
            <div className="text-lg font-medium text-gray-700">
              {getScoreLabel(Number(stability.stability_score))}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-6">
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className={`h-full transition-all ${Number(stability.stability_score) >= 0.95 ? 'bg-green-500' : Number(stability.stability_score) >= 0.85 ? 'bg-primary' : Number(stability.stability_score) >= 0.70 ? 'bg-yellow-500' : 'bg-red-500'}`}
                style={{ width: `${scorePercentage}%` }}
              />
            </div>
          </div>

          {/* Details */}
          <div className="space-y-3 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Comparaisons totales:</span>
              <span className="font-medium">{stability.details.total_field_comparisons}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Résultats identiques:</span>
              <span className="font-medium text-green-600">{stability.details.identical_results}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Résultats différents:</span>
              <span className="font-medium text-orange-600">{stability.details.different_results}</span>
            </div>
          </div>

          {/* Fields with variance */}
          {stability.details.fields_with_variance.length > 0 && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold mb-2">Champs avec variance:</h4>
              <div className="space-y-2">
                {stability.details.fields_with_variance.slice(0, 5).map((field) => (
                  <div key={field.field} className="flex justify-between items-center text-sm">
                    <span className="text-gray-700 truncate">{field.field}</span>
                    <span className="text-orange-600 font-medium">
                      {(Number(field.variance_rate) * 100).toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Interpretation */}
          <div className="mt-4 p-3 bg-gray-50 rounded text-sm text-gray-700">
            <strong>Interprétation: </strong>
            {stability.interpretation}
          </div>
        </>
      )}
    </div>
  );
}
